import { createContext, useContext, useEffect, useState } from "react";
import { useUserData } from "./userContext";
import { fetchAllAsiakasPersonSettingTypes } from "src/data/auth/asiakasAuthClass";
import PropTypes from "prop-types";
import { dailyMessageBoxesGetByAsiakasId } from "src/pages/Grid/GridDailyMessages/gridDailyMessageFunctions";
import { GridContext } from "./GridContext/GridContext";

export const DailyMessageContext = createContext();

export const DailyMessageProvider = ({ children }) => {
  const { socket, userAsiakas, token, isLoaded: userIsLoaded } = useUserData();
  const { time, isLoaded: gridIsLoaded } = useContext(GridContext) || {};
  const [allAsiakasPersonSettingTypes, setAllAsiakasPersonSettingTypes] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [boxes, setBoxes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [boxPermissions, setBoxPermissions] = useState([]);

  useEffect(() => {
    if (!userIsLoaded || !gridIsLoaded) return;
    fetchAllAsiakasPersonSettingTypes(token).then((result) => {
      setAllAsiakasPersonSettingTypes(result);
    });
  }, [userIsLoaded, gridIsLoaded]);

  useEffect(() => {
    if (!userAsiakas || !time || !userIsLoaded || !gridIsLoaded) return;
    init().then(() => {
      setIsLoaded(true);
    });
  }, [userAsiakas, time, userIsLoaded, gridIsLoaded]);

  const init = async () => {
    setIsLoading(true);
    const _result = await dailyMessageBoxesGetByAsiakasId(
      userAsiakas.asiakasId,
      time.starttime,
      token,
      time.yyyymmdd
    );
    const boxes = _result.boxes;
    const messages = _result.messages;
    const boxPermissions = _result.boxPermissions;

    // order boxes by yyyymmdd so that null is first
    boxes.sort((a, b) => {
      if (!a.yyyymmdd && !b.yyyymmdd) return 0;
      if (!a.yyyymmdd) return -1;
      if (!b.yyyymmdd) return 1;
      return 0;
    });

    for (let i = 0; i < boxes.length; i++) {
      const box = boxes[i];
      const boxMessage = messages.find((m) => m.boxId === box.boxId);
      if (boxMessage) {
        box.messageData = {
          yyyymmdd: boxMessage.yyyymmdd,
          message: boxMessage.message,
          readOnly: box.ownerAsiakasId !== userAsiakas.asiakasId,
        };
      }
    }
    setBoxes(boxes);
    setMessages(messages);
    setBoxPermissions(boxPermissions);
    socket.emit(
      "joinRooms",
      boxes.map((box) => "box" + box.boxId + "_" + time.yyyymmdd)
    );
    setIsLoading(false);
  };

  const getPersonSettingType = (typeId) => {
    return allAsiakasPersonSettingTypes.find((type) => type.asiakasPersonSettingTypeId === typeId);
  };

  const setBox = (box) => {
    const newBoxes = boxes.map((b) => {
      if (b.boxId === box.boxId) {
        return box;
      }
      return b;
    });
    setBoxes(newBoxes);
  };

  const setBoxPermission = (boxPermission) => {
    const newBoxPermissions = boxPermissions.map((bp) => {
      if (bp.asiakasPersonSettingTypeId === boxPermission.asiakasPersonSettingTypeId) {
        return boxPermission;
      }
      return bp;
    });
    setBoxPermissions(newBoxPermissions);
  };

  // list of unique dailyMessageBoxAsiakasId and asiakasIds that are in boxPermissions, no duplicates
  const boxAsiakasIdList = boxPermissions.reduce((acc, bp) => {
    if (!acc.find((a) => a.dailyMessageBoxAsiakasId === bp.dailyMessageBoxAsiakasId)) {
      acc.push({
        dailyMessageBoxAsiakasId: bp.dailyMessageBoxAsiakasId,
        asiakasId: bp.asiakasId,
        boxId: bp.boxId,
        readOnly: bp.ownerAsiakasId !== userAsiakas.asiakasId,
      });
    }
    return acc;
  }, []);

  return (
    <DailyMessageContext.Provider
      value={{
        isLoaded,
        getPersonSettingType,
        isLoading,
        boxes,
        setBox,
        boxPermissions,
        setBoxPermission,
        boxAsiakasIdList,
        messages,
        time,

        reload: init, // reload boxes and messages using init()
      }}>
      {children}
    </DailyMessageContext.Provider>
  );
};

export const useDailyMessageData = () => useContext(DailyMessageContext);

DailyMessageProvider.propTypes = {
  children: PropTypes.node,
};
